import { useMemo, useState } from "react";
import type { ApiError } from "@/lib/api";
import type { Player } from "@/lib/types";
import { CoachModal } from "./CoachModal";
import { useCoachUsers, type RosterMutations } from "../hooks/useRoster";

/**
 * Link-account modal — Coach > Roster.
 *
 * Ports the legacy `openCoachLinkModal` (`js/coaching/roster.js`).
 * Links a team user account to a roster player so the player's
 * feedback reaches that account (a guardian, the player's own
 * account, or another related account).
 *
 * `useCoachUsers` (gated open here) loads the team's linkable
 * users; the picker excludes accounts already linked to THIS
 * player so a coach can't double-link the same user. The
 * relationship enum mirrors `PlayerUserLink.relationship` —
 * `guardian` / `self` / `other`; the backend's
 * `CreatePlayerUserLinkRequest` validates against
 * `_VALID_PLAYER_RELATIONSHIPS`.
 */

const RELATIONSHIP_OPTIONS: ReadonlyArray<{
  value: "guardian" | "self" | "other";
  label: string;
}> = [
  { value: "guardian", label: "Guardian / family" },
  { value: "self", label: "The player's own account" },
  { value: "other", label: "Other" },
];

export type LinkAccountModalProps = {
  player: Player;
  mutations: RosterMutations;
  onClose: () => void;
  onSaved?: () => void;
};

export function LinkAccountModal({
  player,
  mutations,
  onClose,
  onSaved,
}: LinkAccountModalProps) {
  // Fetch the team's linkable users only while this modal is open.
  const usersQuery = useCoachUsers(true);
  const [userId, setUserId] = useState("");
  const [relationship, setRelationship] = useState<
    "guardian" | "self" | "other"
  >("guardian");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Exclude accounts already linked to this player.
  const linkedUserIds = useMemo(
    () => new Set((player.links ?? []).map((l) => l.user_id)),
    [player.links],
  );
  const availableUsers = useMemo(
    () =>
      (usersQuery.data?.users ?? []).filter((u) => !linkedUserIds.has(u.id)),
    [usersQuery.data, linkedUserIds],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) {
      setError("Pick an account to link.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await mutations.linkAccount({
        player_id: player.id,
        user_id: userId,
        relationship,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : "Could not link the account.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CoachModal
      title={`Link an account — ${player.display_name}`}
      onClose={onClose}
      data-testid="roster-link-modal"
    >
      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Account
          </span>
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            data-testid="link-modal-user"
            disabled={usersQuery.isLoading}
            className="tktic-select"
          >
            <option value="">
              {usersQuery.isLoading
                ? "Loading accounts…"
                : availableUsers.length === 0
                  ? "No linkable accounts"
                  : "Select an account…"}
            </option>
            {availableUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_name} · @{u.username}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Relationship
          </span>
          <select
            value={relationship}
            onChange={(e) =>
              setRelationship(
                e.target.value as "guardian" | "self" | "other",
              )
            }
            data-testid="link-modal-relationship"
            className="tktic-select"
          >
            {RELATIONSHIP_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="link-modal-error"
          >
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || availableUsers.length === 0}
          data-testid="link-modal-submit"
          className="bg-accent hover:bg-accent-hover disabled:bg-bg-card disabled:text-ink-muted text-white rounded px-4 py-2 text-sm font-display uppercase tracking-wide transition-colors"
        >
          {submitting ? "Linking…" : "Link account"}
        </button>
      </form>
    </CoachModal>
  );
}
